import { useState, useEffect, useCallback } from "react";
import { useConfettiStore } from "@/store/confettiStore";

interface UseConfettiOnceParams {
  /**
   * Whether the condition for showing confetti is met (e.g., max volume reached)
   */
  shouldTrigger: boolean;
  /**
   * The date when the current period ends (used to determine if we're in a new period)
   */
  nextWindowDate?: Date;
  /**
   * Campaign name to track confetti per campaign
   */
  campaignName?: string;
  /**
   * Duration of the confetti animation in milliseconds
   */
  duration?: number;
}

interface UseConfettiOnceReturn {
  /**
   * Whether confetti should be actively shown
   */
  isActive: boolean;
  /**
   * Whether confetti has been shown in this session (prevents showing on reload)
   */
  hasShownInSession: boolean;
}

/**
 * Hook to manage confetti that should only show once per period
 *
 * Shows confetti when a trigger condition is met, but only once per period.
 * Uses localStorage to persist across page reloads, and automatically clears
 * when the nextWindowDate passes (new period starts).
 *
 * @example
 * ```tsx
 * const { isActive } = useConfettiOnce({
 *   shouldTrigger: isMaxReached,
 *   nextWindowDate: cooldownEndDate,
 *   campaignName: "campaign-1",
 *   duration: 6000,
 * });
 *
 * return isActive && <Confetti />;
 * ```
 */
export const useConfettiOnce = ({
  shouldTrigger,
  nextWindowDate,
  campaignName,
  duration = 6000,
}: UseConfettiOnceParams): UseConfettiOnceReturn => {
  const nextWindowTimestamp = nextWindowDate?.getTime();
  const [isActive, setIsActive] = useState(false);
  const [hasShownInSession, setHasShownInSession] = useState(false);
  const storedNextWindowTimestamp = useConfettiStore((state) =>
    campaignName ? state.campaigns[campaignName] : undefined,
  );
  const markCampaign = useConfettiStore((state) => state.markCampaign);
  const clearCampaign = useConfettiStore((state) => state.clearCampaign);

  /**
   * Check localStorage to determine if confetti should be shown
   */
  const shouldShowConfetti = useCallback((): boolean => {
    if (nextWindowTimestamp == null || Number.isNaN(nextWindowTimestamp) || !campaignName) return false;

    if (!storedNextWindowTimestamp) return true;

    const now = Date.now();

    if (now > storedNextWindowTimestamp) {
      clearCampaign(campaignName);
      return true;
    }

    if (storedNextWindowTimestamp === nextWindowTimestamp) {
      return false;
    }

    return true;
  }, [nextWindowTimestamp, campaignName, storedNextWindowTimestamp, clearCampaign]);

  /**
   * Save to localStorage that confetti was shown for this period
   */
  const saveConfettiShown = useCallback(() => {
    if (nextWindowTimestamp == null || Number.isNaN(nextWindowTimestamp) || !campaignName) return;

    markCampaign(campaignName, nextWindowTimestamp);
  }, [nextWindowTimestamp, campaignName, markCampaign]);

  /**
   * Main effect: Show confetti when trigger condition is met
   * Only shows once per session and once per period
   */
  useEffect(() => {
    if (!shouldTrigger || hasShownInSession || !shouldShowConfetti()) {
      return;
    }

    setIsActive(true);
    setHasShownInSession(true);
    saveConfettiShown();

    // Auto-hide after duration
    const timeout = setTimeout(() => {
      setIsActive(false);
    }, duration + 100);

    return () => clearTimeout(timeout);
  }, [shouldTrigger, hasShownInSession, shouldShowConfetti, saveConfettiShown, duration]);

  return {
    isActive,
    hasShownInSession,
  };
};
